import {
  CognitoIdentityProviderClient,
  CompleteWebAuthnRegistrationCommand,
  DeleteWebAuthnCredentialCommand,
  InitiateAuthCommand,
  ListWebAuthnCredentialsCommand,
  RespondToAuthChallengeCommand,
  StartWebAuthnRegistrationCommand,
  type WebAuthnCredentialDescription,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { getAccessToken } from './auth';

/**
 * Phase 7 - WebAuthn passkey support for the moderation UI.
 *
 * Cognito's choice-based USER_AUTH flow lets a moderator sign in with a
 * passkey (Touch ID, Windows Hello, security key, or a cross-device
 * mobile authenticator) instead of email + password + TOTP. The
 * passkey path is opt-in: a moderator first signs in normally, then
 * registers a passkey on /settings/security. Subsequent sign-ins offer
 * the passkey as the first factor; cancelling falls back to password.
 *
 * This module talks to Cognito via the AWS SDK v3 (the legacy
 * amazon-cognito-identity-js SDK does not expose the WebAuthn commands)
 * and uses @simplewebauthn/browser for the browser-side ceremony.
 *
 * After a successful sign-in we bridge the resulting tokens back into
 * amazon-cognito-identity-js by constructing a CognitoUserSession and
 * calling setSignInUserSession, so the rest of the app (which reads
 * `pool.getCurrentUser()`) picks them up transparently.
 */

let cachedClient: CognitoIdentityProviderClient | null = null;

function getClient(): CognitoIdentityProviderClient {
  if (cachedClient) return cachedClient;
  const region =
    (import.meta.env.VITE_COGNITO_REGION as string | undefined) ||
    'ap-southeast-2';
  cachedClient = new CognitoIdentityProviderClient({ region });
  return cachedClient;
}

function getClientId(): string {
  const id = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!id) {
    throw new Error('VITE_COGNITO_CLIENT_ID is not configured');
  }
  return id;
}

function getUserPoolId(): string {
  const id = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  if (!id) {
    throw new Error('VITE_COGNITO_USER_POOL_ID is not configured');
  }
  return id;
}

/** Used by tests to reset module state. */
export function __resetWebAuthnCache(): void {
  cachedClient = null;
}

/**
 * Returns true when the current browser supports WebAuthn well enough
 * to register/use a passkey. Components should hide passkey UI when
 * this is false to avoid a confusing dead-end.
 */
export function isPasskeySupported(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Registration (authenticated; access token required)
// ---------------------------------------------------------------------------

export interface RegisterPasskeyResult {
  credentialId: string;
}

/**
 * Register a new passkey for the currently signed-in user. Triggers
 * the browser's passkey-creation prompt (Face ID / Touch ID / Windows
 * Hello / security-key tap).
 *
 * Throws if the user is not signed in, cancels the prompt, or the
 * authenticator returns an error. Callers should surface the error
 * message to the user; the most useful ones come from
 * @simplewebauthn/browser and Cognito directly.
 */
export async function registerPasskey(): Promise<RegisterPasskeyResult> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('You must be signed in to register a passkey.');
  }
  const client = getClient();
  const start = await client.send(
    new StartWebAuthnRegistrationCommand({ AccessToken: accessToken }),
  );
  const optionsJSON = start.CredentialCreationOptions;
  if (!optionsJSON) {
    throw new Error('Cognito did not return passkey registration options.');
  }
  // @simplewebauthn/browser and the AWS SDK both speak the W3C JSON
  // shape; their TypeScript types just don't line up because one is
  // strongly typed and the other is a generic JSON document. Cast
  // through unknown at the boundary.
  const attestation = await startRegistration({
    optionsJSON: optionsJSON as unknown as Parameters<
      typeof startRegistration
    >[0]['optionsJSON'],
  });
  // Round-trip through JSON to satisfy the AWS SDK's recursive
  // `DocumentType` shape (an any-JSON union that does not structurally
  // accept the strongly-typed RegistrationResponseJSON).
  const credentialJson = JSON.parse(JSON.stringify(attestation));
  await client.send(
    new CompleteWebAuthnRegistrationCommand({
      AccessToken: accessToken,
      Credential: credentialJson,
    }),
  );
  return { credentialId: attestation.id };
}

// ---------------------------------------------------------------------------
// Listing + deletion (authenticated)
// ---------------------------------------------------------------------------

export interface PasskeySummary {
  credentialId: string;
  name: string;
  authenticatorAttachment?: string;
  transports: string[];
  createdAt?: Date;
}

function toSummary(c: WebAuthnCredentialDescription): PasskeySummary {
  return {
    credentialId: c.CredentialId ?? '',
    name: c.FriendlyCredentialName ?? 'Unnamed passkey',
    authenticatorAttachment: c.AuthenticatorAttachment,
    transports: c.AuthenticatorTransports ?? [],
    createdAt: c.CreatedAt,
  };
}

/** List the current user's registered passkeys. */
export async function listPasskeys(): Promise<PasskeySummary[]> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('You must be signed in to view passkeys.');
  }
  const client = getClient();
  const out = await client.send(
    new ListWebAuthnCredentialsCommand({ AccessToken: accessToken }),
  );
  return (out.Credentials ?? []).map(toSummary);
}

/** Remove a passkey by Cognito credential ID. */
export async function deletePasskey(credentialId: string): Promise<void> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('You must be signed in to remove a passkey.');
  }
  const client = getClient();
  await client.send(
    new DeleteWebAuthnCredentialCommand({
      AccessToken: accessToken,
      CredentialId: credentialId,
    }),
  );
}

// ---------------------------------------------------------------------------
// Sign-in (unauthenticated start; WebAuthn challenge)
// ---------------------------------------------------------------------------

/**
 * Discriminated result of a passkey sign-in attempt. `ok: true` means
 * tokens have been bridged into the amazon-cognito-identity-js session
 * and the caller can navigate into the app. `ok: false` returns a
 * reason the caller can use to either fall back to password sign-in
 * (most cases) or surface a hard error.
 */
export type SignInWithPasskeyResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'cancelled' | 'no-passkey' | 'not-supported' | 'unknown';
      error?: Error;
    };

/**
 * Attempt a passwordless sign-in using a passkey. The caller passes an
 * email (Cognito needs it to scope the WebAuthn challenge to the
 * user's registered credentials); on success the user's session is
 * persisted and `getCurrentUser()` in auth.ts reflects them
 * immediately.
 */
export async function signInWithPasskey(
  email: string,
): Promise<SignInWithPasskeyResult> {
  if (!isPasskeySupported()) {
    return { ok: false, reason: 'not-supported' };
  }
  const clientId = getClientId();
  const client = getClient();

  let initiate;
  try {
    initiate = await client.send(
      new InitiateAuthCommand({
        AuthFlow: 'USER_AUTH',
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: 'WEB_AUTHN',
        },
      }),
    );
  } catch (e) {
    // Most likely UserNotFoundException with PreventUserExistenceErrors
    // turned on, or a network error. Either way, fall back to password.
    return { ok: false, reason: 'no-passkey', error: e as Error };
  }

  if (initiate.ChallengeName !== 'WEB_AUTHN' || !initiate.ChallengeParameters) {
    // Cognito decided not to serve a WebAuthn challenge (the user has
    // no passkey registered, or the pool is misconfigured). Fall back.
    return { ok: false, reason: 'no-passkey' };
  }

  const requestOptionsRaw =
    initiate.ChallengeParameters.CREDENTIAL_REQUEST_OPTIONS;
  if (!requestOptionsRaw) {
    return { ok: false, reason: 'unknown' };
  }
  let requestOptions: Parameters<typeof startAuthentication>[0]['optionsJSON'];
  try {
    requestOptions = JSON.parse(requestOptionsRaw);
  } catch {
    return { ok: false, reason: 'unknown' };
  }

  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: requestOptions });
  } catch (e) {
    // The user cancelled the OS picker (NotAllowedError) or no
    // matching credential was available. Either way, give the caller
    // a chance to offer the password path.
    const err = e as Error;
    if (err?.name === 'NotAllowedError') {
      return { ok: false, reason: 'cancelled', error: err };
    }
    return { ok: false, reason: 'unknown', error: err };
  }

  let respond;
  try {
    respond = await client.send(
      new RespondToAuthChallengeCommand({
        ChallengeName: 'WEB_AUTHN',
        ClientId: clientId,
        Session: initiate.Session,
        ChallengeResponses: {
          USERNAME: email,
          CREDENTIAL: JSON.stringify(assertion),
        },
      }),
    );
  } catch (e) {
    return { ok: false, reason: 'unknown', error: e as Error };
  }

  const result = respond.AuthenticationResult;
  if (!result?.IdToken || !result.AccessToken || !result.RefreshToken) {
    return { ok: false, reason: 'unknown' };
  }

  // Bridge the SDK tokens back into amazon-cognito-identity-js so the
  // rest of the app (Login screen, hooks, getCurrentUser, getIdToken,
  // etc.) sees a fresh authenticated session.
  bridgeSdkTokensIntoLegacySession(email, {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
  });

  return { ok: true };
}

interface BridgeTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * Construct a CognitoUserSession from raw tokens and attach it to a
 * fresh CognitoUser for the given email. The legacy SDK persists the
 * session to localStorage via its internal `storage` member, so the
 * next `pool.getCurrentUser()` call returns the user with valid
 * tokens.
 */
function bridgeSdkTokensIntoLegacySession(
  email: string,
  tokens: BridgeTokens,
): void {
  // The legacy SDK persists tokens via its CognitoUser.storage member
  // (localStorage by default). Building a CognitoUser tied to a pool
  // with the same UserPoolId + ClientId as auth.ts uses means that
  // auth.ts's getPool().getCurrentUser() will return this user on its
  // next read.
  const pool = new CognitoUserPool({
    UserPoolId: getUserPoolId(),
    ClientId: getClientId(),
  });
  const user = new CognitoUser({ Username: email, Pool: pool });
  user.setSignInUserSession(
    new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }),
    }),
  );
}
